import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import type { BackendModule, ReadCallback } from 'i18next'
import api from './api'
import { PLATFORM_VOCABULARY, type Vocabulary } from '@/shared/lib/vocabulary'

/**
 * Custom i18next backend that loads translations from the platform API.
 * Fetches merged translations (global + tenant cascade) for the current tenant.
 */
const ApiBackend: BackendModule = {
  type: 'backend',

  init() {
    // no-op
  },

  read(language: string, _namespace: string, callback: ReadCallback) {
    // Started off a resolved promise so a synchronous throw lands in the same
    // catch as a rejection - loading translations must never take the app down.
    Promise.resolve()
      .then(() => api.get('/translations/', { params: { language, source: 'FE' } }))
      .then((res) => {
        callback(null, res.data)
      })
      .catch((err) => {
        // Fall back gracefully - keys render as-is if API is unavailable
        console.warn('[i18n] Failed to load translations:', err)
        callback(null, {})
      })
  },
}

i18n
  .use(ApiBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false, // React escapes by default
      // Catalogue copy carries the tenant's noun as a slot
      // ({{warrantyNounLower}}) rather than the platform's word. Seeded with
      // the platform words so a slot never renders empty before the org
      // context lands, or on a surface that has no tenant (login).
      defaultVariables: { ...PLATFORM_VOCABULARY },
    },
    react: {
      useSuspense: false,
    },
  })

/**
 * Swap in a tenant's own words. Called once the org context resolves.
 *
 * i18next reads `defaultVariables` live off its options on every `t()`, so
 * assigning is enough to change what renders - but react-i18next only
 * re-renders on an event, hence the emit.
 */
export function setVocabulary(vocabulary: Vocabulary | undefined) {
  const next = { ...PLATFORM_VOCABULARY, ...(vocabulary ?? {}) }
  const current = i18n.options.interpolation?.defaultVariables as
    | Vocabulary
    | undefined
  const unchanged =
    current &&
    Object.keys(next).length === Object.keys(current).length &&
    Object.entries(next).every(([key, value]) => current[key] === value)
  if (unchanged) return

  i18n.options.interpolation = { ...i18n.options.interpolation, defaultVariables: next }
  i18n.emit('languageChanged', i18n.language)
}

export default i18n
